import { criterionLabel } from "@components/EvalRubric/constants";

/** Shared enums, labels and key bindings for the eval review panel (PROC-188). */

/** Failure tags under the score grid, ordered by keyboard layout of their
 *  shortcuts. Only what no deterministic rule can decide; keep the list short. */
export const FAILURE_TAGS: ReadonlyArray<{ value: string; label: string; key: string }> = [
  { value: "made_up_detail", label: "made-up detail", key: "q" },
  { value: "wrong_number", label: "wrong number", key: "w" },
  { value: "wrong_name", label: "wrong name", key: "e" },
  { value: "missing_detail", label: "missing detail", key: "r" },
  { value: "wrong_tone", label: "wrong tone", key: "t" },
];

/** The 1-5 scale every dimension is scored on. */
export const SCORE_VALUES = [1, 2, 3, 4, 5] as const;

/** Single-key bindings, no modifiers. The handler and the help text both
 *  read this, so they cannot drift. */
export const REVIEW_KEYS = {
  SCORE_MIN: "1",
  SCORE_MAX: "5",
  NEXT_DIMENSION: "j",
  PREV_DIMENSION: "k",
  // Arrows mirror j/k; the handler returns early inside an input, so the
  // caret still moves in the notes field.
  NEXT_DIMENSION_ARROW: "ArrowDown",
  PREV_DIMENSION_ARROW: "ArrowUp",
  NOTES: "n",
  SAVE: "Enter",
  HELP: "?",
} as const;

/** How a deterministic check's verdict renders. */
export const CheckStatus = {
  PASS: "pass",
  FAIL: "fail",
  INFO: "info",
} as const;

export type CheckStatusValue = (typeof CheckStatus)[keyof typeof CheckStatus];

/** ``passed: null`` = diagnostic — it must not render as a failure. */
export const checkStatus = (passed: boolean | null): CheckStatusValue => {
  if (passed === null) return CheckStatus.INFO;
  return passed ? CheckStatus.PASS : CheckStatus.FAIL;
};

/** Design-system positive/negative pairings — no semantic success *fill*
 *  token is exported from the UI package. */
export const CHECK_STYLES: Record<CheckStatusValue, string> = {
  [CheckStatus.PASS]: "bg-green-100 text-green-700",
  [CheckStatus.FAIL]: "bg-red-100 text-red-600",
  [CheckStatus.INFO]: "bg-bg-default text-text-secondary",
};

/** Selected score pill — deliberately neutral: tinting scores red/green
 *  would anchor the reviewer's own input. */
export const SCORE_SELECTED = "border-rio-600 bg-rio-600 text-white";

/** Mirrors the ``source`` values from ``eval_review_service.scoring_standard``. */
export const StandardSource = {
  RUBRIC: "rubric",
  DEFAULT: "default",
} as const;

/** Mirrors ``EvalCheckResult.MetricType`` in api-django. */
export const MetricType = {
  RULE: "rule",
  JUDGE: "judge",
} as const;

/** The judge's 1–5 score, from ``details.value`` with the normalized
 *  ``score`` as fallback. Null for rule checks — they have no scale. */
export const judgeScoreValue = (check: { metric_type: string; score: number; details?: unknown }): number | null => {
  if (check.metric_type !== MetricType.JUDGE) return null;
  const value = (check.details as { value?: unknown } | null)?.value;
  if (typeof value === "number") return value;
  return Math.round(check.score * 4 + 1);
};

/** A failure at the rubric's bottom anchor disqualifies the output outright. */
export const DISQUALIFYING_JUDGE_VALUE = 1;

export const isDisqualifyingFailure = (check: {
  metric_type: string;
  score: number;
  passed: boolean | null;
  details?: unknown;
}): boolean => check.passed === false && judgeScoreValue(check) === DISQUALIFYING_JUDGE_VALUE;

/** Human labels for the checks — each names the finding, not the check. */
export const METRIC_LABELS: Record<string, string> = {
  placeholder_leakage: "Leftover template text",
  schema_compliance: "Required output shape",
  numeric_grounding: "Numbers traceable to the input",
  score_descriptor_agreement: "Stated score levels",
};

/** Checks that no longer run — both retracted as wrong, so their old
 *  failures on historical runs must not be surfaced or aggregated. */
export const RETIRED_METRICS: ReadonlySet<string> = new Set(["content_sufficiency", "name_consistency"]);

/** A check's display name; falls back to humanising the key. */
export const metricLabel = (metric: string): string => {
  if (metric.startsWith("judge_")) {
    // Same criterion lookup the score grid uses, so the labels match.
    return criterionLabel(metric.slice("judge_".length));
  }
  return METRIC_LABELS[metric] ?? metric.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
};

/** Verdicts for the checks that reach one — only `schema_compliance`; the
 *  rest observe (see `RULE_OBSERVATIONS`). */
export const RULE_VERDICTS: Record<string, { pass: string; fail: string }> = {
  schema_compliance: { pass: "matches", fail: "does not match" },
};

/** Wording for observational checks — facts, never verdicts (the judge
 *  scores them). `detailsKey` is where the finding lives in the details. */
export const RULE_OBSERVATIONS: Record<string, { detailsKey: string; some: (n: number) => string; none: string }> = {
  numeric_grounding: {
    detailsKey: "ungrounded",
    some: (n) => `${n} not in the source`,
    none: "all in the source",
  },
  placeholder_leakage: {
    detailsKey: "matches",
    some: (n) => `${n} found`,
    none: "none found",
  },
  score_descriptor_agreement: {
    detailsKey: "problems",
    some: (n) => `${n} disagree with Vineland bands`,
    none: "match Vineland bands",
  },
};

/** How many things an observational check found, or null if it is not one. */
export const observationCount = (check: { metric_name: string; details?: unknown }): number | null => {
  const spec = RULE_OBSERVATIONS[check.metric_name];
  if (!spec) return null;
  const found = (check.details as Record<string, unknown> | null | undefined)?.[spec.detailsKey];
  return Array.isArray(found) ? found.length : 0;
};

/** Checks whose PASS carries no information, so it is not shown. Empty for now. */
export const FAIL_ONLY_METRICS: ReadonlySet<string> = new Set<string>();

/** Whether a check should appear at all — skipped means it found nothing it
 *  could evaluate, which is not a result. */
export const isCheckWorthShowing = (check: {
  metric_name: string;
  passed: boolean | null;
  details?: unknown;
}): boolean => {
  // Retired checks still arrive from stale workers; they are not actionable.
  if (RETIRED_METRICS.has(check.metric_name)) return false;
  const skipped = (check.details as { skipped?: boolean } | null | undefined)?.skipped;
  if (skipped) return false;
  if (FAIL_ONLY_METRICS.has(check.metric_name) && check.passed !== false) return false;
  return true;
};

/** The one-word verdict for a check, never blank — missing wording used to
 *  render an empty chip. */
export const ruleVerdict = (check: { metric_name: string; passed: boolean | null; details?: unknown }): string => {
  const verdicts = RULE_VERDICTS[check.metric_name];
  if (verdicts) return check.passed === false ? verdicts.fail : verdicts.pass;

  const spec = RULE_OBSERVATIONS[check.metric_name];
  if (spec) {
    const count = observationCount(check) ?? 0;
    return count > 0 ? spec.some(count) : spec.none;
  }

  return check.passed === false ? "failed" : check.passed === null ? "no result" : "passed";
};
